import { useCallback, useMemo, useRef, useState } from 'react'

import { Lookup, useLookup } from '@/providers'
import IconButton from '@components/IconButton/IconButton'
import { faCheck, faX } from '@fortawesome/pro-light-svg-icons'
import {
	Box,
	MenuItem,
	Paper,
	Popper,
	Stack,
	TextField,
	TextFieldProps,
	useTheme,
} from '@mui/material'

type Props = {
	value?: { operator: string; value: string } | null
	label: string
	onChange?: ({ operator, value }: { operator: string; value: string }) => void
} & Omit<TextFieldProps, 'onChange'>

export default function NumericOperatorInput({
	value = null,
	label,
	onChange = () => {},
	...textFieldProps
}: Props) {
	const [panelOpen, setPanelOpen] = useState(false)

	const anchorEl = useRef<HTMLElement | null>(null)

	const operatorOptions = useLookup(Lookup.Operators)

	const displayValue = useMemo(() => {
		if (!value) return ''
		const operatorLabel = operatorOptions.find((op) => op.id === value.operator)?.label || ''
		return `${operatorLabel} ${value.value}`
	}, [value, operatorOptions])

	const togglePanel = useCallback(() => {
		if (panelOpen) {
			setPanelOpen(false)
			return
		}
		setPanelOpen(true)
	}, [panelOpen])

	const handleChange = useCallback(
		(value: { operator: string; value: string }) => {
			onChange(value)
			togglePanel()
		},
		[onChange, togglePanel]
	)

	return (
		<Box width="100%">
			<TextField
				{...textFieldProps}
				inputRef={(el) => {
					anchorEl.current = el
				}}
				label={label}
				value={displayValue}
				fullWidth
				onClick={togglePanel}
				slotProps={{ htmlInput: { readOnly: true } }}
			/>
			<Popper open={panelOpen} anchorEl={anchorEl.current} placement="bottom-start">
				<NumericOperatorPanel operators={operatorOptions} value={value} onChange={handleChange} />
			</Popper>
		</Box>
	)
}

type NumericOperatorPanelProps = {
	operators: SimpleListItem[]
	value: { operator: string; value: string } | null
	onChange: ({ operator, value }: { operator: Id; value: string }) => void
}

// TODO: Panel currently displaying behind other elements, needs z-index adjustment

export function NumericOperatorPanel({ operators, value, onChange }: NumericOperatorPanelProps) {
	const [operatorInput, setOperatorInput] = useState(
		operators.find((opt) => opt.id === value?.operator) || null
	)
	const [valueInput, setValueInput] = useState(value?.value || '')
	const theme = useTheme()

	const handleSubmit = useCallback(() => {
		onChange({ operator: operatorInput.id, value: valueInput })
	}, [operatorInput, valueInput, onChange])

	const handleOperatorChange = useCallback(
		(id: string) => {
			const selected = operators.find((op) => op.id === id) || null
			setOperatorInput(selected)
		},
		[operators]
	)

	const handleValueChange = useCallback((value: string) => {
		setValueInput(value)
	}, [])

	const handleClear = useCallback(() => {
		setOperatorInput(null)
		setValueInput('')
		onChange(null)
	}, [onChange])

	return (
		<Paper sx={{ width: '26rem' }}>
			<Stack p={2} direction="row" spacing={2} alignItems="center">
				<IconButton
					size="small"
					icon={faX}
					sx={{ color: theme.palette.error.main }}
					onClick={handleClear}
				/>
				<TextField
					size="small"
					label="Type"
					sx={{ width: '10rem' }}
					select
					value={operatorInput?.id || ''}
					onChange={(e) => handleOperatorChange(e.target.value)}
				>
					{operators.map((operator) => (
						<MenuItem key={operator.id} value={operator.id}>
							{operator.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					label="Value"
					sx={{ width: '14rem' }}
					type="number"
					value={valueInput}
					onChange={(e) => handleValueChange(e.target.value)}
				/>
				<IconButton
					disabled={!operatorInput || !valueInput}
					size="small"
					sx={{ color: theme.palette.success.main }}
					icon={faCheck}
					onClick={handleSubmit}
				/>
			</Stack>
		</Paper>
	)
}
